/**
 * Appointments tab — lists every occurrence of the current recurring
 * series with full per-row actions. Flutter parity:
 * `view_edit_appointment/tab_widget/appointments_tab.dart` (1565 lines).
 *
 * Mounted only when the host appointment is part of a recurring series
 * (`appointment.recurring?.id`) — the page tab strip hides it otherwise.
 *
 * Scope (PROD-1019 — full parity):
 *   - Columns: leading icon cell (profile-colour bar · location · recurring ·
 *     comment · flags · missing-data alert) · Date · Time · Doctor · Room ·
 *     RFV · Status · Note · actions.
 *   - Per-row kebab (SeriesAppointmentRowActions): Add Note, Edit, Reschedule
 *     {this/following/all}, Cancel {this/following/all}; View for inactive rows.
 *   - Status filter (multi-select + chips) + Location filter.
 *   - Sortable headers + client-side pagination.
 *   - Loading / empty / error states.
 *
 * The list is fetched once (a recurring series is small); filter / sort /
 * paging are client-side over that set.
 */

import { useMemo, useState, type CSSProperties } from 'react';
import { AlertTriangle, ChevronDown, ChevronLeft, ChevronRight, ChevronUp, Flag, MessageSquare, Repeat } from 'lucide-react';
import { StatusBadge, appointmentStatusFromCode } from '@scripsteam/scrips-signal-ds';
import { Icon, Skeleton } from '@scripsteam/scrips-signal-ds';
import { AnchoredPopover, AnchoredPopoverContent, AnchoredPopoverTrigger } from '@scripsteam/scrips-signal-ds';
import { Avatar } from '@scripsteam/scrips-signal-ds';
import { appointmentEdgeColor, locationGlyph } from '@/scheduling/lib/appointment-glyphs';
import { cn } from '@/lib/utils';
import { usePermission } from '@/auth/permission-gate';
import { useAuthStore } from '@/auth/auth-store';
import { useRecurringAppointmentList } from '@/scheduling/services/use-recurring-appointment-list';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
import { SeriesAppointmentRowActions } from './series-appointment-row-actions';
import type { AppointmentResponse } from '@/types/scheduling-api';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

interface ViewAppointmentAppointmentsTabProps {
  appointment: AppointmentResponse | null;
}

type SortKey = 'date' | 'time' | 'doctor' | 'room' | 'rfv' | 'status' | 'note';
type SortDir = 'asc' | 'desc';

const PAGE_SIZES = [10, 25, 50] as const;

export function ViewAppointmentAppointmentsTab({
  appointment,
}: ViewAppointmentAppointmentsTabProps) {
  const recurringId = appointment?.recurring?.id;

  const canView = usePermission(['Manage_Patient_Appointments'], { view: true });
  const canEdit = usePermission(['Manage_Patient_Appointments'], { edit: true });
  const currentUserId = useAuthStore((s) => s.user?.userId) ?? '';

  const { data, isLoading, isError, refetch } = useRecurringAppointmentList({
    recurringId,
    pageNumber: 1,
    pageSize: 200, // fetch the whole series; filter/sort/page client-side.
  });

  // Filters
  const [statusFilter, setStatusFilter] = useState<Set<string>>(new Set());
  const [locationFilter, setLocationFilter] = useState<Set<string>>(new Set());
  // Sort
  const [sortKey, setSortKey] = useState<SortKey>('date');
  const [sortDir, setSortDir] = useState<SortDir>('asc');
  // Paging
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState<number>(10);

  const all = (data?.appointments ?? []) as AppointmentResponse[];
  // We fetch a single large page (pageSize 200) and filter/sort/page client-
  // side — a recurring series is normally small. Guard the rare giant series:
  // the response's `count` is the server-side total, so if it exceeds what we
  // fetched, surface that the tail isn't shown rather than silently dropping it.
  const totalCount = data?.count ?? all.length;
  const truncated = totalCount > all.length;

  // Distinct statuses / locations present in the series — drive the filters.
  const statusOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of all) {
      const code = a.status?.code;
      if (code) m.set(code, a.status?.display ?? code);
    }
    return [...m.entries()].map(([code, display]) => ({ code, display }));
  }, [all]);

  const locationOptions = useMemo(() => {
    const m = new Map<string, string>();
    for (const a of all) {
      const id = a.location?.id;
      if (id) m.set(id, a.location?.name ?? id);
    }
    return [...m.entries()].map(([id, name]) => ({ id, name }));
  }, [all]);

  const filtered = useMemo(() => {
    let rows = all;
    if (statusFilter.size > 0) {
      rows = rows.filter((a) => a.status?.code && statusFilter.has(a.status.code));
    }
    if (locationFilter.size > 0) {
      rows = rows.filter((a) => a.location?.id && locationFilter.has(a.location.id));
    }
    const sorted = [...rows].sort((a, b) => cmp(a, b, sortKey));
    if (sortDir === 'desc') sorted.reverse();
    return sorted;
  }, [all, statusFilter, locationFilter, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageRows = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  };

  if (!recurringId) {
    return (
      <div className="appt-series-center">
        This appointment isn't part of a recurring series.
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="appt-series-page">
        <Skeleton className="h-4 w-1/4" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (isError) {
    return (
      <div className="appt-series-center" data-error="">
        Couldn't load the recurring series.
      </div>
    );
  }

  return (
    <div className="appt-series-page" data-slot="appt-series">
      {/* Toolbar — status filter + location filter + count (the design's PaApptSeriesTab `.toolbar`) */}
      <div className="appt-series-toolbar">
        <h2 className="appt-series-title">
          Series Appointments
        </h2>

        <StatusFilter
          options={statusOptions}
          selected={statusFilter}
          onChange={(next) => {
            setStatusFilter(next);
            setPage(1);
          }}
        />

        {locationOptions.length > 1 ? (
          <LocationFilter
            options={locationOptions}
            selected={locationFilter}
            onToggle={(id) => {
              setLocationFilter((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              });
              setPage(1);
            }}
          />
        ) : null}

        <p className="appt-series-count">
          {filtered.length} occurrence{filtered.length === 1 ? '' : 's'}
        </p>
      </div>

      {truncated ? (
        <p className="appt-series-info">
          Showing the first {all.length} of {totalCount} occurrences. Open the
          series from the calendar to view the rest.
        </p>
      ) : null}

      {/* Selected-status chips */}
      {statusFilter.size > 0 ? (
        <div className="appt-series-chips">
          {[...statusFilter].map((code) => {
            const opt = statusOptions.find((o) => o.code === code);
            return (
              <button
                key={code}
                type="button"
                onClick={() => {
                  setStatusFilter((prev) => {
                    const next = new Set(prev);
                    next.delete(code);
                    return next;
                  });
                }}
                className="appt-series-chip"
              >
                {opt?.display ?? code}
                <Icon name="close" size={12} aria-hidden />
              </button>
            );
          })}
        </div>
      ) : null}

      {filtered.length === 0 ? (
        <p className="appt-series-empty">
          {all.length === 0
            ? 'No occurrences in this series yet.'
            : 'No occurrences match the current filters.'}
        </p>
      ) : (
        <>
          <div className="appt-series-table-wrap scrollbar-hide">
            <table className="appt-series-table">
              <thead>
                <tr>
                  <Th>{''}</Th>
                  <SortableTh label="Date" active={sortKey === 'date'} dir={sortDir} onClick={() => toggleSort('date')} />
                  <SortableTh label="Time" active={sortKey === 'time'} dir={sortDir} onClick={() => toggleSort('time')} />
                  <SortableTh label="Doctor" active={sortKey === 'doctor'} dir={sortDir} onClick={() => toggleSort('doctor')} />
                  <SortableTh label="Room" active={sortKey === 'room'} dir={sortDir} onClick={() => toggleSort('room')} />
                  <SortableTh label="RFV" active={sortKey === 'rfv'} dir={sortDir} onClick={() => toggleSort('rfv')} />
                  <SortableTh label="Status" active={sortKey === 'status'} dir={sortDir} onClick={() => toggleSort('status')} />
                  <SortableTh label="Note" active={sortKey === 'note'} dir={sortDir} onClick={() => toggleSort('note')} />
                  <Th>{''}</Th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((occ) => {
                  const isCurrent = Boolean(
                    appointment?.id && occ.id && occ.id === appointment.id,
                  );
                  const provider = providerOf(occ);
                  return (
                    <tr
                      key={occ.id}
                      data-current={isCurrent || undefined}
                      title={isCurrent ? 'This is the appointment you opened' : undefined}
                    >
                      <Td>
                        <LeadingIcons occ={occ} />
                      </Td>
                      <Td>{fmtDate(occ.start)}</Td>
                      <Td className="whitespace-nowrap">{fmtTimeRange(occ.start, occ.end)}</Td>
                      <Td>
                        <div className="flex items-center gap-2 min-w-0">
                          <Avatar
                            scale="compact"
                            name={provider?.name ?? '—'}
                            src={(provider as { photo?: string } | undefined)?.photo}
                            size="sm"
                            tone="provider"
                          />
                          <span className="truncate max-w-35">{provider?.name ?? 'N/A'}</span>
                        </div>
                      </Td>
                      <Td>{occ.room?.name ?? 'N/A'}</Td>
                      <Td className="truncate max-w-40">{occ.reasonCode?.display ?? 'N/A'}</Td>
                      <Td>
                        {occ.status ? (
                          <StatusBadge
                            status={appointmentStatusFromCode(occ.status.code)}
                            label={occ.status.display ?? occ.status.code}
                            className="text-xs px-2 py-px"
                          />
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td className="truncate max-w-40">
                        {occ.comment ? (
                          <span className="text-status-error">{occ.comment}</span>
                        ) : (
                          '—'
                        )}
                      </Td>
                      <Td className="text-right">
                        <SeriesAppointmentRowActions
                          occ={occ}
                          canView={canView}
                          canEdit={canEdit}
                          currentUserId={currentUserId}
                          onChanged={() => void refetch()}
                        />
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="appt-series-foot">
            <div className="appt-series-rpp">
              <span>Rows per page</span>
              <select
                value={pageSize}
                onChange={(e) => {
                  setPageSize(Number(e.target.value));
                  setPage(1);
                }}
                className="appt-series-select"
              >
                {PAGE_SIZES.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <div className="appt-series-pager">
              <span>
                Page {safePage} of {totalPages}
              </span>
              <button
                type="button"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="appt-series-page-btn"
                aria-label="Previous page"
              >
                <ChevronLeft className="size-4" />
              </button>
              <button
                type="button"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                className="appt-series-page-btn"
                aria-label="Next page"
              >
                <ChevronRight className="size-4" />
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ─── Toolbar filters ────────────────────────────────────────────────────

function StatusFilter({
  options,
  selected,
  onChange,
}: {
  options: { code: string; display: string }[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = selected.size > 0;
  return (
    <AnchoredPopover open={open} onOpenChange={setOpen}>
      <AnchoredPopoverTrigger
        render={
          <button
            type="button"
            className="appt-series-filter"
            data-active={active || undefined}
          />
        }
      >
        <Icon name="filter" size={16} aria-hidden />
        Statuses
      </AnchoredPopoverTrigger>
      <AnchoredPopoverContent collisionAvoidance={{ fallbackAxisSide: 'end' }} align="start" side="bottom" className="appt-series-pop">
        <div className="appt-series-pop-head">
          <span className="appt-series-pop-title">
            Appointment Status
          </span>
          <div className="appt-series-pop-links">
            <button
              type="button"
              className="appt-series-link"
              onClick={() => onChange(new Set(options.map((o) => o.code)))}
            >
              Select All
            </button>
            <button
              type="button"
              className="appt-series-link"
              disabled={!active}
              onClick={() => onChange(new Set())}
            >
              Reset
            </button>
          </div>
        </div>
        <div className="flex flex-col gap-1">
          {options.map((o) => {
            const checked = selected.has(o.code);
            return (
              <label key={o.code} className="appt-series-pop-row">
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => {
                    const next = new Set(selected);
                    if (checked) next.delete(o.code);
                    else next.add(o.code);
                    onChange(next);
                  }}
                />
                {o.display}
              </label>
            );
          })}
          {options.length === 0 ? (
            <span className="text-sm text-text-muted">No statuses.</span>
          ) : null}
        </div>
      </AnchoredPopoverContent>
    </AnchoredPopover>
  );
}

function LocationFilter({
  options,
  selected,
  onToggle,
}: {
  options: { id: string; name: string }[];
  selected: Set<string>;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      {options.map((o) => {
        const active = selected.has(o.id);
        const FilterGlyph = locationGlyph(o.name);
        return (
          <button
            key={o.id}
            type="button"
            onClick={() => onToggle(o.id)}
            className="appt-series-loc"
            data-active={active || undefined}
            title={`Filter: ${o.name}`}
          >
            <FilterGlyph className="size-4" aria-hidden />
            {o.name}
          </button>
        );
      })}
    </div>
  );
}

// ─── Leading icon cell ──────────────────────────────────────────────────

function LeadingIcons({ occ }: { occ: AppointmentResponse }) {
  const RowLocationGlyph = locationGlyph(occ.location?.name);
  // Practice's rule (15 Oct): the profile's colour is the slim bar, on the muted ramp at 500; the location mark is neutral.
  const color = appointmentEdgeColor(
    (occ.appointmentProfile as { color?: string } | undefined)?.color,
    occ.location?.name,
  );
  const hasFlags = ((occ as { flagList?: unknown[] }).flagList?.length ?? 0) > 0;
  const missing = missingDataAlert(occ);
  return (
    <div className="appt-series-lead">
      <span
        className="appt-series-bar bg-[var(--pa-flag-color)]"
        style={{ '--pa-flag-color': color } as CSSProperties}
        aria-hidden
      />
      <span title={occ.location?.name ?? undefined} className="inline-flex">
        <RowLocationGlyph className="size-4 text-text-muted" aria-hidden />
      </span>
      <span title="Recurring appointment" className="inline-flex">
        <Repeat className="size-4 text-text-secondary" aria-hidden />
      </span>
      {occ.comment ? (
        <span title={occ.comment} className="inline-flex">
          <MessageSquare className="size-4 text-text-secondary" aria-hidden />
        </span>
      ) : null}
      {hasFlags ? (
        <span title="Flagged" className="inline-flex">
          <Flag className="size-4 text-text-secondary" aria-hidden />
        </span>
      ) : null}
      {missing ? (
        <span role="img" aria-label="Missing patient info" className="appt-series-warn"><AlertTriangle className="size-4" aria-hidden /></span>
      ) : null}
    </div>
  );
}

// ─── Table cells ────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="appt-series-th">
      {children}
    </th>
  );
}

function SortableTh({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) {
  return (
    <th className="appt-series-th">
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'inline-flex items-center gap-1 hover:text-text-primary',
          active && 'text-brand-primary',
        )}
      >
        {label}
        {active ? (
          dir === 'asc' ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />
        ) : null}
      </button>
    </th>
  );
}

function Td({ children, className }: { children: React.ReactNode; className?: string }) {
  return <td className={cn('appt-series-td', className)}>{children}</td>;
}

// ─── Helpers ──────────────────────────────────────────────────────────────

function providerOf(occ: AppointmentResponse) {
  return occ.participant?.find((p) => {
    const t = (p as { referenceType?: string }).referenceType?.toLowerCase();
    return t === 'provider' || t === 'practitioner';
  })?.provider;
}

function cmp(a: AppointmentResponse, b: AppointmentResponse, key: SortKey): number {
  const s = (x?: string | null) => (x ?? '').toString();
  switch (key) {
    case 'date':
    case 'time':
      return s(a.start).localeCompare(s(b.start));
    case 'doctor':
      return s(providerOf(a)?.name).localeCompare(s(providerOf(b)?.name));
    case 'room':
      return s(a.room?.name).localeCompare(s(b.room?.name));
    case 'rfv':
      return s(a.reasonCode?.display).localeCompare(s(b.reasonCode?.display));
    case 'status':
      return s(a.status?.display).localeCompare(s(b.status?.display));
    case 'note':
      return s(a.comment).localeCompare(s(b.comment));
    default:
      return 0;
  }
}

function computeAge(dob?: string): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const m = now.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

/** Flutter `displayAppointmentLocationFlagsIcon` red alert gate: minor without
 *  guardian ID, OR adult without identification ID, OR insurance not added. */
function missingDataAlert(occ: AppointmentResponse): boolean {
  const patient = findPatientParticipant(occ.participant)?.patient as
    | {
        dateOfBirth?: string;
        birthDate?: string;
        age?: string;
        identificationId?: string;
        isInsuranceAdded?: boolean;
        guardianDetails?: { guaardianIdentificationId?: string };
      }
    | undefined;
  if (!patient) return false;
  const age = computeAge(patient.dateOfBirth ?? patient.birthDate);
  if (age == null) {
    // No DOB to reason about minors — only the insurance signal is reliable.
    return patient.isInsuranceAdded === false;
  }
  const isMinor = age < 18;
  const guardianId = patient.guardianDetails?.guaardianIdentificationId;
  const needsGuardianId = isMinor && !guardianId;
  const needsPatientId = !isMinor && !patient.identificationId;
  const needsInsurance = patient.isInsuranceAdded === false;
  return needsGuardianId || needsPatientId || needsInsurance;
}

function fmtDate(iso?: string): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      weekday: 'short',
      day: '2-digit',
      month: 'short',
    });
  } catch {
    return iso;
  }
}

function fmtTimeRange(startISO?: string, endISO?: string): string {
  if (!startISO) return '—';
  const fmt = (iso: string) =>
    new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  return endISO ? `${fmt(startISO)} – ${fmt(endISO)}` : fmt(startISO);
}

// Status → badge mapping now uses the canonical `appointmentStatusFromCode`
// (imported from the StatusBadge atom) — single source across the app.
